// <athos-recs>: Athos recommendation engine picks products, rendered as jam-cards via ?view=card.
(() => {
  if (customElements.get('athos-recs')) return;

  const CARD_TIMEOUT_MS = 8000;

  class AthosRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.wired) return;
      this.dataset.wired = 'true';
      this.results = this.querySelector('[data-athos-recs-results]');
      this.status = this.querySelector('[data-athos-recs-status]');
      if (!window.AthosClient || !window.AthosCards || !this.results) return this.fail();
      if (!this.dataset.siteId) {
        console.error('[athos-recs] Missing Athos site ID. Set it in Theme settings → Athos / Searchspring → “Athos site ID”.');
        return this.fail();
      }
      /* Aborts the in-flight chain if the section is torn down, so a late response can't inject. */
      this.controller = new AbortController();
      this.client = window.AthosClient.createClient({ siteId: this.dataset.siteId });
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load() {
      const { signal } = this.controller;
      try {
        const limit = Number.parseInt(this.dataset.productLimit, 10) || 6;
        const handles = await this.client.fetchRecommendations(
          {
            profileTag: this.dataset.profileTag,
            /* Over-fetch by one so filtering out the current product still leaves a full row. */
            limit: limit + 1,
            productId: this.dataset.productId,
            /* lastViewed omitted: the client sources it from the theme's recently-viewed list. */
          },
          { signal },
        );
        if (signal.aborted) return;

        /* Never recommend the product you're already on (parity with the native recently-viewed). */
        const currentHandle = this.dataset.productHandle;
        const filtered = (currentHandle ? handles.filter((handle) => handle !== currentHandle) : handles).slice(0, limit);
        if (!filtered.length) return this.empty();

        const cards = await window.AthosCards.fetchCards(filtered, { signal, timeoutMs: CARD_TIMEOUT_MS });
        if (signal.aborted) return;
        const html = cards.filter(Boolean).join('');
        if (!html) return this.empty();

        this.results.insertAdjacentHTML('beforeend', html);
        window.DisplayGroupSwatches?.register(this.results);
        this.setState('ready');
        this.announce(this.results.querySelectorAll('.jam-card').length);
      } catch (error) {
        if (signal.aborted || error?.name === 'AbortError') return;
        this.fail(error);
      }
    }

    /* Announce the outcome once through the status region, not the whole card grid. */
    announce(count) {
      if (!this.status || !count) return;
      const template = this.dataset.statusLabel || '{count} recommended products loaded';
      this.status.textContent = template.replace('{count}', String(count));
    }

    setState(state) {
      this.dataset.athosState = state;
      this.setAttribute('aria-busy', 'false');
    }

    empty() {
      this.setState('empty');
      this.hidden = true;
    }

    fail(error) {
      this.setState('error');
      this.hidden = true;
      if (error) console.error('[Athos Recommendations]', error);
    }
  }

  customElements.define('athos-recs', AthosRecommendations);
})();
